import { ErrorCode } from '@trulyinbox/errors';

/**
 * Copy + error codes for the view-only mode a PAUSED subscription puts the
 * whole panel into.
 *
 * A non-component module on purpose (`apps/web/CLAUDE.md` Fast-Refresh rule):
 * these are constants, and co-locating them in a `.tsx` would disqualify that
 * file from Fast Refresh.
 */

/**
 * ONE condition, TWO backend codes, and every consumer that branches on
 * "paused" must accept both.
 *
 * `040006` is the general view-only gate (the interceptor, `ApiKeyGuard`, the
 * `ResourceControlGuard` deny-path mapping, the bulk `ENABLE_WARMUP` chunk).
 * `020009` is the older connect/reconnect thrower, kept because it has already
 * reached an environment. This set is the single place the web knows there are
 * two — never compare against one of them at a call site.
 */
export const PAUSED_ERROR_CODES: ReadonlySet<string> = new Set([
  ErrorCode.Billing.Shared.SubscriptionPaused,
  ErrorCode.EmailAccounts.Shared.SubscriptionPaused,
]);

/**
 * Why a control is disabled, by viewer. An owner can fix it themselves; a
 * member cannot, and telling them to "resume it from Billing" would send them
 * to a page whose buttons are owner-only.
 */
export const PAUSED_REASON_OWNER =
  'Your subscription is paused. Resume it from Billing to make changes.';

export const PAUSED_REASON_MEMBER =
  "Your organization's subscription is paused. Ask your workspace owner to resume it.";

/** The toast shown when a write is rejected because the subscription is paused. */
export const PAUSED_TOAST_MESSAGE =
  'Your subscription is paused, so your account is view-only. Resume your subscription to make changes.';

/** The label + CTA a locked paid view shows while paused, instead of "Upgrade". */
export const PAUSED_LOCK_TITLE = 'Your subscription is paused';

export const PAUSED_LOCK_OWNER_CTA = 'Resume subscription';

export function pausedReasonFor(isOrgOwner: boolean): string {
  return isOrgOwner ? PAUSED_REASON_OWNER : PAUSED_REASON_MEMBER;
}

export function isPausedErrorCode(errorCode: string | null | undefined): boolean {
  return typeof errorCode === 'string' && PAUSED_ERROR_CODES.has(errorCode);
}
